import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Home, Search, ArrowLeft, Dices } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const popularPages = [
    { title: "Random Number Generator", path: "/", icon: Dices },
    { title: "Games & Tools", path: "/games", icon: Dices },
    { title: "FAQ", path: "/faq", icon: Search },
    { title: "Learn More", path: "/learn-more", icon: Search },
  ];

  return (
    <>
      <SEO 
        title="404 - Page Not Found | RandomGen"
        description="The page you're looking for doesn't exist. Return to RandomGen to generate random numbers, roll dice, flip coins, and more."
        keywords="404, page not found, random number generator"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center space-y-8">
          {/* Error Message */}
          <div className="space-y-4">
            <h1 className="text-8xl font-bold text-primary animate-pulse">404</h1>
            <h2 className="text-3xl font-semibold text-foreground">Page Not Found</h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              Oops! The page you're looking for doesn't exist. It might have been moved or deleted.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild size="lg" className="gap-2">
              <Link to="/">
                <Home className="w-5 h-5" />
                Back to Home
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="gap-2">
              <Link to="/sitemap">
                <Search className="w-5 h-5" />
                View Sitemap
              </Link>
            </Button>
          </div>

          {/* Popular Pages */}
          <Card className="p-6 bg-card/50 backdrop-blur">
            <h3 className="text-xl font-semibold mb-4 text-foreground">Popular Pages</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {popularPages.map((page) => {
                const Icon = page.icon;
                return (
                  <Button
                    key={page.path}
                    asChild
                    variant="ghost"
                    className="justify-start gap-3 h-auto py-3"
                  >
                    <Link to={page.path}>
                      <Icon className="w-5 h-5 text-primary" />
                      <span>{page.title}</span>
                    </Link>
                  </Button>
                );
              })}
            </div>
          </Card>

          {/* Additional Help */}
          <div className="text-sm text-muted-foreground">
            <p>
              If you believe this is an error, please{" "}
              <Link to="/contact" className="text-primary hover:underline">
                contact us
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;